import { Button } from "components/DesignSystem/Button/Button";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import {
  useAmendFinancialClosingMutation,
  useResolveFinancialClosingRequestMutation,
} from "store/apis/financialClosing";
import { BackendError } from "types/utils/error";
import { ResolveRequestModal } from "./ResolveRequestModal";

type ChangeRequestActionsProps = {
  entityId: string;
  financialClosingId: string;
  onAmended: () => void;
};

export const ChangeRequestActions = ({
  entityId,
  financialClosingId,
  onAmended,
}: ChangeRequestActionsProps) => {
  const { alertToast, successToast } = useToast();
  const resolveRequestModal = useModal();

  const [resolveRequest, { isLoading: isResolvingRequest }] =
    useResolveFinancialClosingRequestMutation();
  const [amendClosing, { isLoading: isAmending }] =
    useAmendFinancialClosingMutation();

  const showError = (error: unknown) =>
    alertToast({
      message:
        (error as BackendError).data?.error?.message ?? "Something went wrong",
    });

  const handleResolveRequest = async () => {
    try {
      await resolveRequest({ entityId, financialClosingId }).unwrap();
      successToast({ message: "Request resolved" });
      resolveRequestModal.close();
    } catch (error) {
      showError(error);
    }
  };

  const handleAmend = async () => {
    try {
      await amendClosing({ entityId, financialClosingId }).unwrap();
      successToast({ message: "New version created" });
      onAmended();
    } catch (error) {
      showError(error);
    }
  };

  return (
    <>
      <Button
        type="button"
        customType="secondary"
        onClick={resolveRequestModal.open}
        disabled={isAmending}
      >
        Resolve request
      </Button>
      <Button
        type="button"
        customType="danger"
        onClick={handleAmend}
        isLoading={isAmending}
        disabled={isAmending}
      >
        Amend & create new version
      </Button>
      <ResolveRequestModal
        open={resolveRequestModal.isOpen}
        isLoading={isResolvingRequest}
        onCancel={resolveRequestModal.close}
        onConfirm={handleResolveRequest}
      />
    </>
  );
};
